import { useState } from 'react';
import { toast } from 'src/components/ui/toast';
import { mutationErrorMessage } from '../task-pages/taskPageAdmin';

export type EntityEditorMessages = {
  saved: string;
  saveFailed: string;
  archived: string;
  archiveFailed: string;
};

export type EntityEditorOptions<TDraft, TCreateInput, TUpdateInput, TEntity> = {
  toCreateInput: (draft: TDraft) => TCreateInput;
  /** Null when the draft is for a not-yet-created entity. */
  toUpdateInput: (draft: TDraft) => TUpdateInput | null;
  create: (input: TCreateInput) => Promise<unknown>;
  update: (input: TUpdateInput) => Promise<unknown>;
  archive: (entity: TEntity) => Promise<unknown>;
  messages: EntityEditorMessages;
};

async function runWithToast(
  run: () => Promise<void>,
  success: string,
  failure: string,
): Promise<void> {
  try {
    await run();
    toast.success(success);
  } catch (error) {
    toast.error(mutationErrorMessage(error, failure));
  }
}

/**
 * The list/create/edit/archive scaffold shared by the org-level page admin
 * cards (task pages, agent pages, page sections): the open editor's draft
 * plus the snapshot it started from (for the unsaved-changes prompt), the
 * pending archive target, and the save/archive actions with their toasts.
 * Cards keep their own RBAC, queries, and JSX.
 */
export function useEntityEditor<TDraft, TCreateInput, TUpdateInput, TEntity>(
  options: EntityEditorOptions<TDraft, TCreateInput, TUpdateInput, TEntity>,
) {
  const [draft, setDraft] = useState<TDraft | null>(null);
  const [initialDraft, setInitialDraft] = useState<TDraft | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TEntity | null>(null);

  function openEditor(nextDraft: TDraft) {
    setDraft(nextDraft);
    setInitialDraft(nextDraft);
  }

  function closeEditor() {
    setDraft(null);
    setInitialDraft(null);
  }

  function save() {
    if (!draft) return;
    const current = draft;
    const updateInput = options.toUpdateInput(current);
    void runWithToast(
      async () => {
        if (updateInput) {
          await options.update(updateInput);
        } else {
          await options.create(options.toCreateInput(current));
        }
        closeEditor();
      },
      options.messages.saved,
      options.messages.saveFailed,
    );
  }

  function archive() {
    if (!archiveTarget) return;
    const target = archiveTarget;
    void runWithToast(
      async () => {
        await options.archive(target);
        setArchiveTarget(null);
      },
      options.messages.archived,
      options.messages.archiveFailed,
    );
  }

  return {
    draft,
    initialDraft,
    setDraft,
    openEditor,
    closeEditor,
    save,
    archiveTarget,
    setArchiveTarget,
    archive,
  };
}
